/**
 * Base control component utilities and wrappers.
 *
 * Provides consistent styling and structure for all setting controls.
 * All controls should use ControlWrapper for DRY and consistent UI.
 */

import type { ReactNode } from "react";

/**
 * Supported value types for controls.
 */
export type ControlValue = number | boolean | string | number[] | null;

/**
 * Base props interface for all control components.
 * Generic type T must extend ControlValue to ensure type safety.
 */
export interface BaseControlProps<T extends ControlValue = ControlValue> {
  label: string;
  value: T;
  description?: string;
  error?: string;
  onChange: (value: T) => void;
  disabled?: boolean;
}

/**
 * Wrapper component for consistent control layout.
 *
 * Handles:
 * - Label rendering
 * - Description text
 * - Error message display
 * - Consistent spacing and styling
 * - Test IDs for all elements
 */
export function ControlWrapper({
  label,
  description,
  error,
  children,
  testId,
}: {
  label: string;
  description?: string;
  error?: string;
  children: ReactNode;
  testId?: string;
}) {
  const baseTestId =
    testId || `control-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <div className="mb-4" data-testid={baseTestId}>
      <label
        className="text-sm font-medium block mb-2"
        data-testid={`${baseTestId}-label`}
      >
        {label}
      </label>

      {description && (
        <p
          className="text-xs text-gray-400 mb-3"
          data-testid={`${baseTestId}-description`}
        >
          {description}
        </p>
      )}

      <div data-testid={`${baseTestId}-content`}>{children}</div>

      {error && (
        <p
          className="text-xs text-red-400 mt-2"
          data-testid={`${baseTestId}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
}
